import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import { drawSignatureImageInPdfCell, isSigImage, toSigDataUrl } from "./sf009FormSignatureUtils";
import ThemeYnSelect from "../forms/ThemeYnSelect";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const PDF_EXTRA_BLANK_ROWS = 2;

const LIMITATIONS_CHOICES = [
  { value: "Yes", label: "Yes" },
  { value: "No", label: "No" },
];

function normalizeRegisterSignature(val) {
  if (!isSigImage(val)) return "";
  const s = String(val).trim();
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

function normalizeLimitationsToAccess(val) {
  const s = String(val ?? "").trim();
  if (s === "Yes" || s === "No") return s;
  const lower = s.toLowerCase();
  if (lower === "yes") return "Yes";
  if (lower === "no") return "No";
  return "";
}

function emptyRegisterRow() {
  return {
    name: "",
    company: "",
    tradeJobDescription: "",
    proofOfCompetence: "",
    registeredAndExpiryDates: "",
    signatureCompetentPersonE: "",
    signatureAuthorisedPersonE: "",
    limitationsToAccess: "",
  };
}

const DEFAULT_FORM_DATA = {
  projectName: "",
  projectNo: "",
  siteLocation: "",
  sheetNo: "",
  ttslResponsiblePerson: "",
  ttslAuthorisedPersonForSite: "",
  registerEntries: [],
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeRegisterRow(src) {
  const row = src && typeof src === "object" ? src : {};
  return {
    name: row.name ?? "",
    company: row.company ?? "",
    tradeJobDescription: row.tradeJobDescription ?? row.trade_job_description ?? "",
    proofOfCompetence: row.proofOfCompetence ?? row.proof_of_competence ?? "",
    registeredAndExpiryDates:
      row.registeredAndExpiryDates ?? row.registered_and_expiry_dates ?? row.registeredExpiryDates ?? "",
    signatureCompetentPersonE: normalizeRegisterSignature(
      row.signatureCompetentPersonE ??
        row.signatureTtslCompetentPersonE ??
        row.signature_competent_person_e
    ),
    signatureAuthorisedPersonE: normalizeRegisterSignature(
      row.signatureAuthorisedPersonE ??
        row.signatureTtslAuthorisedPersonE ??
        row.signature_authorised_person_e
    ),
    limitationsToAccess: normalizeLimitationsToAccess(row.limitationsToAccess ?? row.limitations_to_access),
  };
}

function registerRowSigKey(row) {
  return [row.name, row.company, row.tradeJobDescription]
    .map((v) => String(v ?? "").trim().toLowerCase())
    .join("||");
}

function buildRegisterSignatureLock(entries) {
  const map = new Map();
  (entries || []).forEach((row) => {
    const key = registerRowSigKey(row);
    if (!key || key === "||||") return;
    map.set(key, {
      signatureCompetentPersonE: normalizeRegisterSignature(row.signatureCompetentPersonE),
      signatureAuthorisedPersonE: normalizeRegisterSignature(row.signatureAuthorisedPersonE),
    });
  });
  return map;
}

function applyLockedRegisterSignatures(entries, lockMap) {
  if (!lockMap || lockMap.size === 0) return entries;
  return entries.map((row) => {
    const locked = lockMap.get(registerRowSigKey(row));
    if (!locked) return row;
    return {
      ...row,
      signatureCompetentPersonE: locked.signatureCompetentPersonE || row.signatureCompetentPersonE,
      signatureAuthorisedPersonE: locked.signatureAuthorisedPersonE || row.signatureAuthorisedPersonE,
    };
  });
}

function hasRegisterRowData(row) {
  if (!row || typeof row !== "object") return false;
  return [
    row.name,
    row.company,
    row.tradeJobDescription,
    row.proofOfCompetence,
    row.registeredAndExpiryDates,
    row.signatureCompetentPersonE,
    row.signatureAuthorisedPersonE,
    row.limitationsToAccess,
  ].some((v) => v != null && String(v).trim() !== "");
}

function registerEntriesFromRaw(entries) {
  const list = Array.isArray(entries) ? entries.map(normalizeRegisterRow) : [];
  return list.filter(hasRegisterRowData);
}

function cloneRegisterEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizeRegisterRow) : [];
}

function entriesForPdf(entries) {
  const filled = registerEntriesFromRaw(entries);
  const rows = [...filled];
  for (let i = 0; i < PDF_EXTRA_BLANK_ROWS; i++) rows.push(emptyRegisterRow());
  return rows;
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  const entries = src.registerEntries ?? src.competentPersons ?? src.entries;
  return {
    projectName: src.projectName ?? hd.projectName ?? "",
    projectNo: src.projectNo ?? hd.projectNo ?? "",
    siteLocation: src.siteLocation ?? hd.siteLocation ?? src.site ?? "",
    sheetNo: src.sheetNo ?? hd.sheetNo ?? "",
    ttslResponsiblePerson: src.ttslResponsiblePerson ?? hd.ttslResponsiblePerson ?? "",
    ttslAuthorisedPersonForSite:
      src.ttslAuthorisedPersonForSite ?? hd.ttslAuthorisedPersonForSite ?? hd.ttslAuthorisedPerson ?? "",
    registerEntries: registerEntriesFromRaw(entries),
  };
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009HdrLblWrap = {
  ...sf009LblCell,
  whiteSpace: "normal",
  lineHeight: 1.35,
  fontSize: 11,
  verticalAlign: "middle",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 10,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 11,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
const sf009SigCell = {
  ...sf009RegCell,
  textAlign: "center",
  verticalAlign: "middle",
  padding: "4px",
  minWidth: 90,
};

function RegisterSignatureDisplay({ value }) {
  const url = normalizeRegisterSignature(value);
  if (!url) {
    return <span style={{ color: "#a0aec0", fontSize: 10 }}>—</span>;
  }
  return (
    <img
      src={url}
      alt="Signature"
      style={{
        maxWidth: "100%",
        maxHeight: 52,
        objectFit: "contain",
        display: "block",
        margin: "0 auto",
      }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
}

function Sf009GRegisterOfCompetentPersonsPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [registerSignatureLock, setRegisterSignatureLock] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-g-register-of-competent-persons", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [
      fd.projectName,
      fd.projectNo,
      fd.siteLocation,
      fd.sheetNo,
      fd.ttslResponsiblePerson,
      f.name,
    ].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.ttslResponsiblePerson || "";
      return next;
    });
  };

  const updateRegisterRow = (rowIndex, field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneRegisterEntries(next.formData.registerEntries);
      entries[rowIndex] = { ...entries[rowIndex], [field]: value };
      next.formData.registerEntries = entries;
      next.name = next.formData.ttslResponsiblePerson || "";
      return next;
    });
  };

  const addRegisterRow = () => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      next.formData.registerEntries = [...cloneRegisterEntries(next.formData.registerEntries), emptyRegisterRow()];
      return next;
    });
  };

  const removeRegisterRow = (rowIndex) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneRegisterEntries(next.formData.registerEntries);
      entries.splice(rowIndex, 1);
      next.formData.registerEntries = entries;
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    const merged = mergeRecordIntoDefaults(raw);
    setRegisterSignatureLock(buildRegisterSignatureLock(merged.registerEntries));
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: merged,
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = editForm.formData || {};
      const entriesToSave = applyLockedRegisterSignatures(
        registerEntriesFromRaw(raw.registerEntries),
        registerSignatureLock
      );
      const res = await fetch(getBaseUrl() + "/api/sf-009-g-register-of-competent-persons/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          registerEntries: entriesToSave,
          name: editForm.name || raw.ttslResponsiblePerson,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        setRegisterSignatureLock(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-g-register-of-competent-persons/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const recordRaw = getRawFormData(record);
    const raw = mergeRecordIntoDefaults(recordRaw);
    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const headerHeight = 30;

    const safePdfDate = (v) => {
      if (!v) return "";
      const d = parseSf009DateForPicker(v);
      if (!d) return String(v);
      return d.toLocaleDateString("en-GB");
    };

    doc.setFillColor(...THEME_RGB);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Register of Competent Persons (Electrical)", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let yPosition = headerHeight + 8;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255], fontSize: 7.5 };
    const pdfVal = { fillColor: PDF_WHITE, fontSize: 7.5 };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 7,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 7, valign: "middle", minCellHeight: 10 };

    const regColW = {
      0: contentW * 0.11,
      1: contentW * 0.11,
      2: contentW * 0.12,
      3: contentW * 0.12,
      4: contentW * 0.14,
      5: contentW * 0.14,
      6: contentW * 0.14,
      7: contentW * 0.12,
    };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = headerHeight + 8;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable(
      {
        body: [
          ["Project name:", raw.projectName || "", "Project no:", raw.projectNo || ""],
          ["Site / location:", raw.siteLocation || "", "Sheet no:", raw.sheetNo || ""],
          [
            "TTSL Responsible Person:",
            raw.ttslResponsiblePerson || "",
            "TTSL Authorised Person (E) for the site:",
            raw.ttslAuthorisedPersonForSite || "",
          ],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.22, ...pdfLbl },
          1: { cellWidth: contentW * 0.28, ...pdfVal },
          2: { cellWidth: contentW * 0.22, ...pdfLbl },
          3: { cellWidth: contentW * 0.28, ...pdfVal },
        },
        styles: pdfGrid,
      },
      28
    );

    const pdfEntries = entriesForPdf(raw.registerEntries);
    const pdfRegisterSigCells = [];
    const registerBody = pdfEntries.map((row, rowIdx) => {
      const compSig = normalizeRegisterSignature(row.signatureCompetentPersonE);
      const authSig = normalizeRegisterSignature(row.signatureAuthorisedPersonE);
      if (compSig) pdfRegisterSigCells.push({ row: rowIdx, col: 5, data: compSig });
      if (authSig) pdfRegisterSigCells.push({ row: rowIdx, col: 6, data: authSig });
      return [
        row.name || "",
        row.company || "",
        row.tradeJobDescription || "",
        row.proofOfCompetence || "",
        row.registeredAndExpiryDates || "",
        compSig ? " " : "",
        authSig ? " " : "",
        row.limitationsToAccess || "",
      ];
    });

    ensureSpace(40);
    autoTable(doc, {
      startY: yPosition,
      theme: "grid",
      tableWidth: contentW,
      margin: pdfMargin,
      rowPageBreak: "auto",
      showHead: "everyPage",
      head: [
        [
          {
            content: "Skilled / Competent Person Details",
            colSpan: 5,
            styles: pdfRegHead,
          },
          {
            content:
              "Confirmation that the person is qualified or has suitable experience to carry out the duties of a Competent Person",
            colSpan: 2,
            styles: pdfRegHead,
          },
          { content: "Limitations to access", rowSpan: 2, styles: pdfRegHead },
        ],
        [
          { content: "Name", styles: pdfRegHead },
          { content: "Company", styles: pdfRegHead },
          { content: "Trade / Job Description", styles: pdfRegHead },
          { content: "Proof of Competence", styles: pdfRegHead },
          { content: "Registered and expiry dates", styles: pdfRegHead },
          { content: "Signature of TTSL Competent Person (E)", styles: pdfRegHead },
          { content: "Signature of TTSL Authorised Person (E)", styles: pdfRegHead },
        ],
      ],
      body: registerBody,
      styles: pdfGrid,
      bodyStyles: pdfRegBody,
      headStyles: {
        fillColor: THEME_LIGHT_RGB,
        textColor: THEME_RGB,
        fontStyle: "bold",
        fontSize: 6.5,
        halign: "center",
        valign: "middle",
        lineWidth: 0.2,
      },
      columnStyles: {
        ...regColW,
        5: { ...regColW[5], minCellHeight: 20 },
        6: { ...regColW[6], minCellHeight: 20 },
      },
      didDrawCell: (data) => {
        if (data.section !== "body") return;
        const hit = pdfRegisterSigCells.find((s) => s.row === data.row.index && s.col === data.column.index);
        if (!hit) return;
        drawSignatureImageInPdfCell(doc, data, toSigDataUrl(hit.data), data.column.index);
      },
    });
    yPosition = doc.lastAutoTable.finalY;

    applyFooterAllPages(doc);

    const displayName = raw.ttslResponsiblePerson || record.name || "Register";
    const safeSite = String(raw.projectName || raw.siteLocation || "Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("G", getSf009SharePointFields("G", raw, record, getUserName))
        : `SF009G_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.projectName || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-G Register of Competent Persons PDF generated successfully!");
    }
  };

  const renderRegisterForm = () => {
    const entries = cloneRegisterEntries(fd.registerEntries);
    return (
      <div style={{ border: sf009TblBorder, background: "#fff" }}>
        <div
          style={{
            textAlign: "center",
            padding: "14px 12px",
            fontWeight: 700,
            fontSize: 15,
            color: "#1a202c",
            borderBottom: sf009TblBorder,
          }}
        >
          Register of Competent Persons (Electrical)
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            <tr>
              <td style={{ ...sf009LblCell, width: "22%" }}>Project name:</td>
              <td style={{ ...sf009ValCell, width: "28%" }}>
                <input
                  type="text"
                  value={fd.projectName || ""}
                  onChange={(e) => updateFormData("_flat", "projectName", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={{ ...sf009LblCell, width: "22%" }}>Project no:</td>
              <td style={{ ...sf009ValCell, width: "28%" }}>
                <input
                  type="text"
                  value={fd.projectNo || ""}
                  onChange={(e) => updateFormData("_flat", "projectNo", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={sf009LblCell}>Site / location:</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.siteLocation || ""}
                  onChange={(e) => updateFormData("_flat", "siteLocation", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={sf009LblCell}>Sheet no:</td>
              <td style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.sheetNo || ""}
                  onChange={(e) => updateFormData("_flat", "sheetNo", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={{ ...sf009LblCell, width: "22%" }}>TTSL Responsible Person:</td>
              <td colSpan={3} style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.ttslResponsiblePerson || ""}
                  onChange={(e) => updateFormData("_flat", "ttslResponsiblePerson", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
            <tr>
              <td style={{ ...sf009HdrLblWrap, width: "22%" }}>TTSL Authorised Person (E) for the site:</td>
              <td colSpan={3} style={sf009ValCell}>
                <input
                  type="text"
                  value={fd.ttslAuthorisedPersonForSite || ""}
                  onChange={(e) => updateFormData("_flat", "ttslAuthorisedPersonForSite", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ overflowX: "auto", borderTop: sf009TblBorder }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1200 }}>
            <thead>
              <tr>
                <th colSpan={5} style={sf009RegHeadCell}>
                  Skilled / Competent Person Details
                </th>
                <th colSpan={2} style={sf009RegHeadCell}>
                  Confirmation that the person is qualified or has suitable experience to carry out the duties of a
                  Competent Person
                </th>
                <th rowSpan={2} style={sf009RegHeadCell}>
                  Limitations to access
                </th>
                <th rowSpan={2} style={{ ...sf009RegHeadCell, width: 48 }}>
                  &nbsp;
                </th>
              </tr>
              <tr>
                <th style={sf009RegHeadCell}>Name</th>
                <th style={sf009RegHeadCell}>Company</th>
                <th style={sf009RegHeadCell}>Trade / Job Description</th>
                <th style={sf009RegHeadCell}>Proof of Competence</th>
                <th style={sf009RegHeadCell}>Registered and expiry dates</th>
                <th style={sf009RegHeadCell}>Signature of TTSL Competent Person (E)</th>
                <th style={sf009RegHeadCell}>Signature of TTSL Authorised Person (E)</th>
              </tr>
            </thead>
            <tbody>
              {entries.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ ...sf009RegCell, textAlign: "center", color: "#718096", padding: 20 }}>
                    No rows yet. Click &quot;Add row&quot; to add an entry.
                  </td>
                </tr>
              ) : (
                entries.map((row, idx) => (
                  <tr key={idx}>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.name || ""}
                        onChange={(e) => updateRegisterRow(idx, "name", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.company || ""}
                        onChange={(e) => updateRegisterRow(idx, "company", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.tradeJobDescription || ""}
                        onChange={(e) => updateRegisterRow(idx, "tradeJobDescription", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.proofOfCompetence || ""}
                        onChange={(e) => updateRegisterRow(idx, "proofOfCompetence", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.registeredAndExpiryDates || ""}
                        onChange={(e) => updateRegisterRow(idx, "registeredAndExpiryDates", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009SigCell}>
                      <RegisterSignatureDisplay value={row.signatureCompetentPersonE} />
                    </td>
                    <td style={sf009SigCell}>
                      <RegisterSignatureDisplay value={row.signatureAuthorisedPersonE} />
                    </td>
                    <td style={sf009RegCell}>
                      <ThemeYnSelect
                        choices={LIMITATIONS_CHOICES}
                        value={normalizeLimitationsToAccess(row.limitationsToAccess)}
                        onChange={(v) => updateRegisterRow(idx, "limitationsToAccess", normalizeLimitationsToAccess(v))}
                        placeholder="Select"
                        fontSize={11}
                        emptyColor="#718096"
                      />
                    </td>
                    <td style={{ ...sf009RegCell, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => removeRegisterRow(idx)}
                        title="Remove row"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#e53e3e",
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <>
      <div style={{ background: "#fff", borderRadius: 20, boxShadow: "0px 4px 20px rgba(0,0,0,0.08)", padding: 24, marginBottom: 24 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, flexWrap: "wrap", gap: 16 }}>
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>SF-009-G Register of Competent Persons (Electrical)</h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i className="fa fa-search" style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }} />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{ padding: "10px 12px 10px 36px", borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 14, outline: "none", width: 250, boxSizing: "border-box" }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project Name</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Project No</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Site / Location</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Responsible Person</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Added By</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Updated Date</th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.siteLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.ttslResponsiblePerson || r.name || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button onClick={() => handleEdit(r)} title="Edit" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button type="button" onClick={() => generatePDF(r)} title="Generate PDF" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.projectName || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{ background: "transparent", border: "none", cursor: busy ? "not-allowed" : "pointer", padding: 6, opacity: busy ? 0.6 : 1 }}
                              >
                                <i className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"} style={{ color: "#22c55e", fontSize: 16 }} />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button onClick={() => handleDelete(r._id)} title="Delete" style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}>
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button className="horizon-table-btn" disabled={page === 1} onClick={() => setPage(page - 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === 1 ? "#f7fafc" : "#fff", color: page === 1 ? "#a0aec0" : THEME, cursor: page === 1 ? "not-allowed" : "pointer" }}>
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button className="horizon-table-btn" disabled={page === totalPages} onClick={() => setPage(page + 1)} style={{ padding: "8px 16px", borderRadius: 8, border: "1px solid #e2e8f0", background: page === totalPages ? "#f7fafc" : "#fff", color: page === totalPages ? "#a0aec0" : THEME, cursor: page === totalPages ? "not-allowed" : "pointer" }}>
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-G Register of Competent Persons (Electrical)</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEditModal(false);
                    setRegisterSignatureLock(null);
                  }}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                {renderRegisterForm()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setRegisterSignatureLock(null);
                  }}
                >
                  Cancel
                </button>
                <button type="button" className="btn" style={{ background: THEME, color: "#fff", fontWeight: 600 }} onClick={handleEditSave}>
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-G Register of Competent Persons (Electrical) File"
        importPath="sf-009-g-register-of-competent-persons/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-G Register of Competent Persons (Electrical)</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009GRegisterOfCompetentPersonsPage;
